import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import type { Media } from '@hellenica/shared';

export default function MediaPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ media: Media[] }>({
    queryKey: ['media'],
    queryFn: () => api.get('/v1/admin/media'),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.upload('/v1/admin/media/upload', file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      toast.success('Image uploaded');
    },
    onError: () => toast.error('Upload failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/admin/media/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      toast.success('Image deleted');
    },
  });

  const onDrop = useCallback((files: File[]) => {
    files.forEach((file) => uploadMutation.mutate(file));
  }, [uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
  });

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-8">Media Library</h1>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center mb-8 cursor-pointer transition-colors ${
          isDragActive ? 'border-brand-bronze bg-brand-bronze/5' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        <p className="text-gray-600">
          {isDragActive ? 'Drop files here...' : 'Drag and drop images here, or click to select'}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {data?.media.map((item) => (
          <div key={item.id} className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={`https://hellenica-media.r2.cloudflarestorage.com/${item.r2_key}`}
              alt={item.alt_text || item.filename}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <button
                onClick={() => deleteMutation.mutate(item.id)}
                className="text-white text-sm bg-red-600 px-3 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
